/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import axios from 'axios';
import Request from '../service/urlService.js';
import { SESSION_EXPIRED } from '../const.js';
import { isNotEmpty } from 'tc-client-common/dist/utils/helpers/helpers.js';
import { handleTracingCookie } from '../utils/cookieHelpers.js';
let config = require('../config/' + process.env.NODE_ENV);

const defaults = {
  baseURL: [config.domain, config.mtfs.root, config.apiversion].join(''),
  timeout: 40000,
  withCredentials: true
};

const instance = axios.create(defaults);

instance.interceptors.request.use(function (config) {
  handleTracingCookie();
  return config;
});

instance.interceptors.response.use(function (response) {
  if (response.headers.location) {
    window.location.replace(response.headers.location);
    throw new axios.Cancel(SESSION_EXPIRED);
  }
  return response;
});

const fileInstance = axios.create(Object.assign({ responseType: 'blob' }, defaults, { timeout: 60000 }));

const downloaderinstance = {
  get: function (url) {
    let finalUrl = `${config.domain}/tcmanagementtoolfs${config.apiversion}${url}`;
    window.location.href = finalUrl;
    return new Promise(function (resolve) {
      return resolve({ objectUrl: finalUrl });
    });
  }
};

class RequestService {
  static getInstance() {
    return instance;
  }

  static getPendingPdf(consentId, consentingId) {
    return fileInstance.get(Request.instance().builddocumentPendingPdfUrl(consentId, consentingId));
  }

  static getSignedPdf(consentingId) {
    return fileInstance.get(Request.instance().buildDocumentSignedPdfUrl(consentingId));
  }

  static getAddendumPdf(addendumId) {
    return fileInstance.get(Request.instance().buildAddendumPdfUrl(addendumId));
  }

  static deleteAddendum({ addendum, reason }) {
    return instance.put(Request.instance().buildDeleteAddendumUrl(addendum.id), { reason });
  }

  static getDocumentUnassignedPdfUrl(consentGid) {
    return fileInstance.get(Request.instance().buildDocumentUnassignedPdfUrl(consentGid));
  }

  static getPeopleBySponsor(sponsor, isParticipant, offset, limit) {
    return instance.get(Request.instance().buildPeopleUrl(sponsor, isParticipant, offset, limit));
  }

  static getParticipantsByStudy({ studyGid, status, sites, optionalDocPublished, search, nextPage, pageSize }) {
    let payload = {
      status,
      sites,
      needOptionalAction: isNotEmpty(optionalDocPublished) ? optionalDocPublished : null,
      search
    };

    return instance.post(Request.instance().buildParticipantsByStudyUrl(studyGid, nextPage, pageSize), payload);
  }

  static getAllActiveStudies(userId, sponsor) {
    return instance.get(Request.instance().buildAllActiveStudiesUrl(userId, sponsor));
  }

  static getNotesByStudyGidAndParticipant(studyGid, participantId) {
    return instance.get(Request.instance().buildNotesByStudyGidAndParticipantId(studyGid, participantId));
  }

  static getResponsesData(participantId) {
    return instance.get(Request.instance().buildGetResponsesDataUrl(participantId));
  }

  static saveNewBiosampleCode(participantId, data) {
    const { documentGid, documentVersion, questionId, ...payload } = data;
    return instance.post(Request.instance().buildUpdateBiosampleCodeUrl(documentGid, documentVersion, questionId), {
      ...payload,
      participantId
    });
  }

  static updateBiosampleCode(participantId, data) {
    const { documentGid, documentVersion, questionId, ...payload } = data;
    return instance.put(Request.instance().buildUpdateBiosampleCodeUrl(documentGid, documentVersion, questionId), {
      ...payload,
      participantId
    });
  }

  static removeBiosampleCode(participantId, data) {
    const { documentGid, documentVersion, questionId, ...payload } = data;
    return instance.put(Request.instance().buildUpdateBiosampleCodeUrl(documentGid, documentVersion, questionId), {
      ...payload,
      participantId
    });
  }

  static getDocumentVersion(participantId, data) {
    const { documentGid, documentVersion } = data;
    return instance.get(Request.instance().buildChangeDocumentVersionUrl(participantId, documentGid, documentVersion));
  }

  static getNotesPdfByStudyGidAndParticipantId(studyGid, participantId) {
    return downloaderinstance.get(Request.instance().buildNotesPdfByStudyGidAndParticipantId(studyGid, participantId));
  }

  static getConsentDocuments(studyGid, participantId) {
    return instance.get(Request.instance().buildConsentDocumentsUrl(studyGid, participantId));
  }

  static getSubjectPhiInformation(studyGid, participantId) {
    return instance.get(Request.instance().buildSubjectPhiInformationUrl(studyGid, participantId));
  }

  static getStudyInformation(studyGId) {
    return instance.get(Request.instance().buildStudyInformationUrl(studyGId));
  }

  static getSiteInformation(studyGId, participantId) {
    return instance.get(Request.instance().buildSiteInformationUrl(studyGId, participantId));
  }

  static getConsentDocumentForParticipant(consentGid) {
    return instance.get(Request.instance().buildParticipantDocumentUrl(consentGid));
  }

  static getConsentingOptInState(consentId) {
    return instance.get(Request.instance().buildGetConsentingOptInStateUrl(consentId));
  }

  static postNoteForConsenting(consentingGid, note) {
    return instance.post(Request.instance().buildAddNoteUrl(consentingGid), note);
  }

  static getAllHelpdeskPhoneNumbers() {
    return instance.get(Request.instance().buildHelpdeskAllPhoneNoUrl());
  }

  static getRoles(sponsor) {
    return instance.get(Request.instance().buildGetRolesUrl(sponsor));
  }

  static getUserAssignments(userId, sponsor) {
    return instance.get(Request.instance().buildUserAssignmentsUrl(userId, sponsor));
  }

  static saveAssignment(userId, assignment) {
    return instance.post(Request.instance().buildAddAssignmentUrl(userId), assignment);
  }

  static updateAssignment(assignmentId, assignment) {
    return instance.put(Request.instance().buildUpdateAssignmentUrl(assignmentId), assignment);
  }

  static deleteAssignment(assignmentId, sponsor, payload) {
    return instance.delete(Request.instance().buildDeleteAssignmentUrl(assignmentId, sponsor), { data: payload });
  }

  static updateCanConsentAndPhi(updatedRowData) {
    return instance.put(Request.instance().buildUpdateAssignmentUrl(updatedRowData.assignmentId), updatedRowData);
  }

  static getDocumentStatuses(consentingGid) {
    return instance.get(Request.instance().buildGetDocumentStatusesUrl(consentingGid));
  }

  static saveDocumentStatus(consentingGid, status, isProtected, formData) {
    return instance.post(Request.instance().buildSaveDocumentStatus(consentingGid, status, isProtected), formData);
  }

  static getConsentingJournal(consentingGid) {
    return instance.get(Request.instance().buildConsentingJournalUrl(consentingGid));
  }

  static getJournalPdf(consentingGid) {
    return fileInstance.get(Request.instance().buildConsentingJournalPDFUrl(consentingGid));
  }

  static getMonitorsBySponsor(sponsor, offset, limit) {
    return instance.get(Request.instance().buildMonitorsUrl(sponsor, offset, limit));
  }

  static saveParticipantStatus(payload) {
    return instance.put(Request.instance().buildSaveParticipantStatusUrl(), payload);
  }

  static getFinalParticipantStatuses() {
    return instance.get(Request.instance().buildGetFinalParticipantStatusesUrl());
  }

  static resetParticipantStatus(participantJourneyGid, payload) {
    return instance.put(Request.instance().buildResetParticipantStatusUrl(participantJourneyGid), payload);
  }

  static getParticipantStatuses() {
    return instance.get(Request.instance().buildGetParticipantStatusesUrl());
  }

  static getOverallParticipantStatus(studyGid, sites) {
    return instance.get(Request.instance().buildOverallParticipantStatusUrl(studyGid, sites));
  }

  static getOrganizationsByStudy(studyGid) {
    return instance.get(Request.instance().buildOrganizationsByStudyUrl(studyGid));
  }

  static getOrganizationsBySponsor(sponsor) {
    return instance.get(Request.instance().buildOrganizationsBySponsorUrl(sponsor));
  }

  static getParticipantJourney(participantJourneyGid) {
    return instance.get(Request.instance().buildParticipantJourneyUrl(participantJourneyGid));
  }

  static updateParticipantJourney(participantJourneyGid, payload) {
    return instance.put(Request.instance().buildParticipantJourneyUrl(participantJourneyGid), payload);
  }

  static getParticipantJournal(participantJourneyGid) {
    return instance.get(Request.instance().buildParticipantJournalUrl(participantJourneyGid));
  }

  static getParticipantJournalPDF(participantJourneyGid) {
    return fileInstance.get(Request.instance().buildparticipantJournalPDFUrl(participantJourneyGid));
  }

  static getOptInQuestionsReportCSV(protocolNumber, payload) {
    return fileInstance.post(Request.instance().buildOptInQuestionsReportUrl(protocolNumber), payload);
  }

  static getOptInQuestionsReportAllCSV(protocolNumber, payload) {
    return instance.post(Request.instance().buildOptInQuestionsReportUrlAll(protocolNumber), payload);
  }

  static getAccessRigthsChangesCSV(studyGid, payload) {
    return fileInstance.post(Request.instance().buildAccessRightsChangesReportUrl(studyGid), payload);
  }

  static postSignedPdf(consentingGid, payload) {
    return fileInstance.post(Request.instance().buildUploadSignedPdfUrl(consentingGid), payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  static postChangeAnswers(consentingGid, payload) {
    return fileInstance.post(Request.instance().buildChangeAnswers(consentingGid), payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  static deleteSignedPdf(consentingGid, payload) {
    return instance.put(Request.instance().buildDeleteSignedPdfUrl(consentingGid), payload);
  }

  static getDelegationLog(sponsorGid, studyGid) {
    return instance.get(Request.instance().buildDelegationLogUrl(sponsorGid, studyGid));
  }

  static getDelegationLogExport(sponsorGid, studyGid) {
    return fileInstance.get(Request.instance().buildDelegationLogExportUrl(sponsorGid, studyGid));
  }

  static fetchAvailableDocuments(participantJourneyGid) {
    return instance.get(Request.instance().fetchAvailableDocumentsUrl(participantJourneyGid));
  }

  static fetchConsenting(consentingGid) {
    return instance.get(Request.instance().fetchConsentingUrl(consentingGid));
  }

  static assignParticipantDocument(participantJourneyGid, consentId, operation, payload) {
    return instance.post(
      Request.instance().assignParticipantDocumentUrl(participantJourneyGid, consentId, operation),
      payload
    );
  }

  static getUserById(userId) {
    return instance.get(Request.instance().getUserByIdUrl(userId));
  }

  static checkParticipantHasNewVersions(participantJourneyGid) {
    return instance.get(Request.instance().checkParticipantHasNewVersions(participantJourneyGid));
  }

  static getTenantCookie(tenantId) {
    const tenantCookieUrl = Request.instance().getTenantCookieUrl(tenantId);
    return instance.get(tenantCookieUrl);
  }

  static updateParticipantEmail(participantJourneyGid, payload) {
    return instance.put(Request.instance().buildParticipantEmailUrl(participantJourneyGid), payload);
  }

  static updateParticipantGender(participantId, payload) {
    return instance.put(Request.instance().buildParticipantGenderUrl(participantId), payload);
  }

  static updateParticipantBirthdate(participantId, payload) {
    return instance.put(Request.instance().buildParticipantBirthdateUrl(participantId), payload);
  }

  static updateParticipantAddress(participantId, payload) {
    return instance.put(Request.instance().buildParticipantAddressUrl(participantId), payload);
  }

  static getReports(sponsorGid, userId) {
    return instance.get(Request.instance().getReports(sponsorGid, userId));
  }

  static setQlikSession(userId, sponsorId) {
    return instance.post(Request.instance().setQlikSession(userId, sponsorId));
  }

  static deleteQlikSession() {
    return instance.get(Request.instance().deleteQlikSession());
  }

  static getReportsStatus(sponsorGid) {
    return instance.get(Request.instance().getReportsStatus(sponsorGid));
  }

  static countSignedConsentings(studyGid) {
    const payload = {
      studyGid
    };
    return instance.post(Request.instance().countSignedConsentings(), payload);
  }

  static exportParticipantArchive(studyGid, username, studyProtocolNumber) {
    const payload = {
      studyGid,
      username,
      studyProtocolNumber
    };

    return instance.post(Request.instance().exportParticpantArchive(), payload);
  }

  static cancelExportParticipantArchive({ sponsor, studyGid, username, studyProtocolNumber }) {
    const payload = {
      sponsor,
      studyGid,
      username,
      studyProtocolNumber,
      jobType: 'BATCH_ARCHIVING'
    };

    return instance.post(Request.instance().cancelExportParticipantArchive(), payload);
  }

  static downloadParticipantArchive(studyProtocolNumber, studyGid, username) {
    return downloaderinstance.get(Request.instance().downloadArchive(studyProtocolNumber, studyGid, username));
  }

  static cancelDownloadParticipantArchive(sponsor, studyProtocolNumber, studyGid, username) {
    const payload = {
      sponsor,
      studyProtocolNumber,
      studyGid,
      username
    };

    return instance.post(Request.instance().cancelDownloadArchive(), payload);
  }

  static getBatchProgress(studyGid, username) {
    return instance.get(Request.instance().getBatchProgress(studyGid, username));
  }

  static getBatchArchivingStatus(studyGid, sites = []) {
    return instance.get(Request.instance().getBatchArchivingStatus(studyGid, sites));
  }

  static getDocumentMetadata(consentingGid) {
    return instance.get(Request.instance().buildConsentingMetadataUrl(consentingGid));
  }

  static putSignedPdf(consentingGid, consenting) {
    const { userId, investigatorId, lar, witness, parents, sponsor, status } = consenting;
    const body = { userId, investigatorId, lar, witness, parents, sponsor, status };
    return instance.put(Request.instance().buildSignedPdfUrl(consentingGid), body);
  }

  static getAcsEligibilityStatus() {
    return instance.get(Request.instance().buildAcsEligibilityUrl());
  }

  static postAcsMigrate() {
    return instance.post(Request.instance().buildAcsMigrateUrl());
  }
}

export default RequestService;
